import { useState } from "react";
import { Plus, Pencil, Trash2, Lock, Unlock, KeyRound } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { api } from "../lib/api";
import { run, useLoad } from "../lib/util";
import { Badge, Btn, Field, Input, Select, Table, Empty } from "../components/kit";
import Invites from "./Invites";
import { CredentialsDialog, genPassword } from "./Credentials";

function PasswordDialog({ u, onClose, onDone }) {
  const [p, setP] = useState(genPassword());
  const save = async () => {
    if (await run(() => api.put(`/admin/users/${u.id}/password`, { password: p }), `Senha de ${u.email} alterada`)) {
      onClose();
      onDone({ name: u.name, login: u.email, password: p });
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="user-password-dialog">
        <DialogHeader><DialogTitle>Alterar senha de {u.email}</DialogTitle></DialogHeader>
        <Field label="Nova senha (mín. 8 caracteres)"><div className="flex gap-2"><Input data-testid="user-new-password-input" className="font-mono" type="text" value={p} onChange={(e) => setP(e.target.value)} /><Btn variant="outline" data-testid="user-password-generate" onClick={() => setP(genPassword())}>Gerar</Btn></div></Field>
        <p className="text-xs text-slate-500">Informe a nova senha ao gestor por um canal seguro. Ele poderá trocá-la depois em "Trocar senha".</p>
        <DialogFooter>
          <Btn variant="outline" onClick={onClose}>Cancelar</Btn>
          <Btn data-testid="user-password-save" disabled={p.length < 8} onClick={save}>Salvar senha</Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UserDialog({ initial, schools, onClose, onSaved, onCreated }) {
  const [f, setF] = useState({ email: "", name: "", school_id: "", password: initial.id ? "" : genPassword(), active: true, ...initial });
  const save = async () => {
    const body = { email: f.email, name: f.name, school_id: f.school_id, active: f.active, password: f.password || null };
    const r = await run(() => (f.id ? api.put(`/admin/users/${f.id}`, body) : api.post("/admin/users", body)), "Gestor salvo");
    if (r) {
      onSaved();
      onClose();
      const s = schools.find((x) => x.id === f.school_id);
      if (f.password) onCreated({ school: s?.name, name: f.name, login: r.data.email, password: f.password, whatsapp: s?.whatsapp });
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="user-dialog">
        <DialogHeader><DialogTitle>{f.id ? "Editar gestor" : "Novo gestor"}</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <Field label="Nome"><Input data-testid="user-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
          <Field label="Login (e-mail ou usuário)"><Input data-testid="user-email-input" autoCapitalize="none" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
          <Field label="Escola"><Select data-testid="user-school-select" value={f.school_id} onChange={(e) => setF({ ...f, school_id: e.target.value })} placeholder="Selecione" options={schools.map((s) => [s.id, `${s.name} (${s.code})`])} /></Field>
          <Field label={f.id ? "Nova senha (deixe vazio para manter)" : "Senha inicial (mín. 8)"}><div className="flex gap-2"><Input data-testid="user-password-input" className="font-mono" type="text" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /><Btn variant="outline" data-testid="user-dialog-generate" onClick={() => setF({ ...f, password: genPassword() })}>Gerar</Btn></div></Field>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="user-active-checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} />Acesso ativo</label>
        </div>
        <DialogFooter>
          <Btn variant="outline" onClick={onClose}>Cancelar</Btn>
          <Btn data-testid="user-save-button" onClick={save}>Salvar</Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function UsersTab() {
  const [users, reload] = useLoad(async () => (await api.get("/admin/users")).data, []);
  const [schools] = useLoad(async () => (await api.get("/admin/schools")).data, []);
  const [edit, setEdit] = useState(null);
  const [pwd, setPwd] = useState(null);
  const [cred, setCred] = useState(null);
  const sname = (id) => (schools || []).find((s) => s.id === id)?.name || "—";
  const del = async (u) => {
    if (window.confirm(`Remover o acesso de ${u.email}?`) && (await run(() => api.delete(`/admin/users/${u.id}`), "Gestor removido"))) reload();
  };
  const toggle = async (u) => {
    const msg = u.active ? `Bloquear o acesso de ${u.email}? A sessão atual será encerrada.` : `Desbloquear o acesso de ${u.email}?`;
    if (window.confirm(msg) && (await run(() => api.put(`/admin/users/${u.id}/active`, { active: !u.active }), u.active ? "Acesso bloqueado" : "Acesso desbloqueado"))) reload();
  };
  return (
    <div className="mt-4 space-y-4">
      <div className="flex justify-end"><Btn variant="outline" data-testid="new-user-button" onClick={() => setEdit({})}><Plus className="w-4 h-4" />Criar gestor com senha</Btn></div>
      <Table testid="users-table">
        <thead><tr><th>Nome</th><th>Login</th><th>Escola</th><th>Situação</th><th /></tr></thead>
        <tbody>
          {(users || []).map((u) => (
            <tr key={u.id}>
              <td className="font-semibold">{u.name}</td>
              <td>{u.email}</td>
              <td>{sname(u.school_id)}</td>
              <td><Badge tone={u.active ? "green" : "red"}>{u.active ? "Ativo" : "Bloqueado"}</Badge></td>
              <td className="whitespace-nowrap">
                <Btn variant="ghost" title="Alterar e-mail, nome, escola ou senha" data-testid={`edit-user-${u.email}`} onClick={() => setEdit({ ...u, password: "" })}><Pencil className="w-4 h-4" /></Btn>
                <Btn variant="outline" title="Alterar senha" data-testid={`password-user-${u.email}`} onClick={() => setPwd(u)}><KeyRound className="w-4 h-4" />Senha</Btn>
                <Btn variant={u.active ? "danger" : "outline"} data-testid={`toggle-user-${u.email}`} onClick={() => toggle(u)}>{u.active ? <><Lock className="w-4 h-4" />Bloquear</> : <><Unlock className="w-4 h-4" />Desbloquear</>}</Btn>
                <Btn variant="ghost" data-testid={`delete-user-${u.email}`} onClick={() => del(u)}><Trash2 className="w-4 h-4 text-red-600" /></Btn>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {users && !users.length && <Empty>Nenhum gestor cadastrado.</Empty>}
      {edit && <UserDialog initial={edit} schools={schools || []} onClose={() => setEdit(null)} onSaved={reload} onCreated={setCred} />}
      {pwd && <PasswordDialog u={pwd} onClose={() => setPwd(null)} onDone={(c) => setCred({ ...c, school: sname(pwd.school_id), whatsapp: (schools || []).find((s) => s.id === pwd.school_id)?.whatsapp })} />}
      {cred && <CredentialsDialog cred={cred} onClose={() => setCred(null)} />}
      <Invites schools={schools || []} />
    </div>
  );
}
